import {
  AppstoreOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  FilterOutlined,
  FolderAddOutlined,
  PictureOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Empty, Input, Modal, Pagination, Popover, Select, message } from 'antd'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { VistoeraActionConfirm } from '../../ui/action'
import {
  PCV_IMAGE_ASSET_DND_MIME,
  serializeImageAssetDragPayload,
  setImageAssetDraggingChrome,
  vistoeraDndProps,
} from '../../workspace/contentProduction/canvas/canvasDragContract'
import {
  deleteImagePickerItem,
  getImagePickerItemDetail,
  getImagePickerSummary,
  listImagePickerItems,
  resolveImagePickerSelection,
  siteAssetVariantUrl,
  type ImageAssetPickerPurpose,
  type ImageAssetPickerSelection,
  type ImagePickerDetail,
  type ImagePickerListItem,
  type ImagePickerListResponse,
  type PickerResourceType,
  type PickerSourceFilter,
  type SaasApiError,
} from '../../api'
import {
  uploadSiteAssetIngest,
  siteAssetUploadErrorMessage,
} from '../../siteAssetUpload'
import {
  CANONICAL_SITE_ASSET_UPLOAD_CONCURRENCY,
  mapWithConcurrency,
  mergeUploadedIntoRecentList,
  pickerAntdPaginationTotal,
  pickerDetailToListItem,
  pickerItemMatchesFilters,
  pickerListLimit,
  pickerListRequestParams,
  pickerPageAfterDelete,
  pickerTotalPages,
  PICKER_DEFAULT_PAGE_SIZE,
  selectionAfterUploadBatch,
} from './imageAssetPickerContract'
import { decrementPickerSummary } from './imagePickerOptimisticDelete'
import {
  getCachedPickerCount,
  pickerCountCacheKey,
  setCachedPickerCount,
} from './imagePickerCountCache'
import {
  filterSessionScopedNewPickerIds,
  isMobileCaptureSessionPaired,
  type MobileCaptureSessionMeta,
} from '../../hooks/mobileCapturePickerCompletion'
import AssetUploadActionTile from '../AssetUploadActionTile/AssetUploadActionTile'
import MobileCaptureQrModal from '../MobileCaptureQrModal'
import ImageFolderTree from '../ImageFolderTree/ImageFolderTree'
import ImageFolderBreadcrumb from '../ImageFolderTree/ImageFolderBreadcrumb'
import FolderNameModal, { type FolderNameModalState } from '../ImageFolderTree/FolderNameModal'
import {
  itemMatchesScope,
  scopeAfterLocalUpload,
  teamQueryForScope,
  uploadTargetForScope,
  type PickerTeamScope,
} from '../ImageFolderTree/folderTreeModel'
import { useImagePickerFolders } from './useImagePickerFolders'
import './image-asset-picker.css'

const MOBILE_CAPTURE_POLL_MS = 2000

const PAGE_SIZE_OPTIONS = [20, 40, 80]
const STABLE_EMPTY_SELECTION: string[] = []
type GalleryQueryOverrides = Partial<{
  page: number
  pageSize: number
  sort: 'recent' | 'oldest' | 'name'
  resourceType: PickerResourceType
  source: PickerSourceFilter
  search: string
  silent: boolean
}>

const RESOURCE_FILTER_VALUES: PickerResourceType[] = [
  'all',
  'original',
  'color_background',
  'shadow',
  'transparent_background',
  'flat_rectified',
  'flat_enhanced',
  'ai_generated',
  'crop',
  'other',
]

const SOURCE_FILTER_VALUES: PickerSourceFilter[] = ['all', 'upload', 'vistoera_generated', 'external_import']

const RESOURCE_TYPE_I18N: Record<Exclude<PickerResourceType, 'all'>, string> = {
  original: 'types.original.label',
  color_background: 'types.color.label',
  shadow: 'types.shadow.label',
  transparent_background: 'types.transparent.label',
  flat_rectified: 'types.flat_rectified.label',
  flat_enhanced: 'types.flat_enhanced.label',
  ai_generated: 'types.ai_generated.label',
  crop: 'types.crop.label',
  other: 'types.other.label',
}

function resourceFilterLabel(t: TFunction<'imageAssets'>, value: PickerResourceType): string {
  if (value === 'all') return t('types.all.label')
  return t(RESOURCE_TYPE_I18N[value])
}

function sourceFilterLabel(t: TFunction<'imageAssets'>, value: PickerSourceFilter): string {
  if (value === 'all') return t('filters.allSources')
  return t(`sources.${value}`)
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatPickerCreatedAt(createdAt: string | null, locale: string): string {
  if (!createdAt) return '—'
  const d = new Date(createdAt)
  if (!Number.isFinite(d.getTime())) return '—'
  return d.toLocaleString(locale, { hour12: false })
}

function resolveTarget(purpose: ImageAssetPickerPurpose): 'site_asset' | 'video_gen_asset' | 'ai_chat_asset' {
  if (purpose === 'video_input') return 'video_gen_asset'
  if (purpose === 'ai_chat_input') return 'ai_chat_asset'
  return 'site_asset'
}

function defaultConfirmLabel(purpose: ImageAssetPickerPurpose, count: number, t: TFunction<'imageAssets'>): string {
  switch (purpose) {
    case 'graphic_insert':
      return count > 1 ? t('picker.confirm.graphicInsertMany', { count }) : t('picker.confirm.graphicInsertOne')
    case 'graphic_replace':
      return t('picker.confirm.graphicReplace')
    case 'item_image':
      return count > 1 ? t('picker.confirm.itemImageMany', { count }) : t('picker.confirm.itemImageOne')
    case 'item_cover':
    case 'site_cover':
      return t('picker.confirm.cover')
    case 'video_input':
      return t('picker.confirm.videoInput')
    case 'ai_chat_input':
      return count > 1 ? t('picker.confirm.aiChatMany', { count }) : t('picker.confirm.aiChatOne')
    case 'canvas_insert':
      return count > 1 ? t('picker.confirm.canvasInsertMany', { count }) : t('picker.confirm.canvasInsertOne')
    case 'object_image_processing':
      return count > 1
        ? t('picker.confirm.objectImageProcessingMany', { count })
        : t('picker.confirm.objectImageProcessingOne')
    default:
      return count > 1 ? t('picker.confirm.genericMany', { count }) : t('picker.confirm.genericOne')
  }
}

type UploadJobStatus = 'queued' | 'uploading' | 'processing' | 'done' | 'error'

type UploadJob = {
  localId: string
  fileName: string
  progress: number
  status: UploadJobStatus
  error?: string
}

function isActiveUploadJob(job: UploadJob): boolean {
  return job.status === 'queued' || job.status === 'uploading' || job.status === 'processing'
}

export interface ImageAssetPickerProps {
  open: boolean
  workspaceId: string
  siteId: string
  purpose?: ImageAssetPickerPurpose
  title?: string
  selectionMode?: 'single' | 'multiple'
  maxSelection?: number
  initialSelection?: string[]
  /** Optional consumer override — e.g. Canvas "添加到画布（4）". */
  confirmLabel?: string | ((count: number) => string)
  onConfirm: (selections: ImageAssetPickerSelection[]) => void
  onCancel: () => void
}

export default function ImageAssetPicker({
  open,
  workspaceId,
  siteId,
  purpose = 'generic',
  title,
  selectionMode = 'single',
  maxSelection = selectionMode === 'multiple' ? 20 : 1,
  initialSelection = STABLE_EMPTY_SELECTION,
  confirmLabel: confirmLabelProp,
  onConfirm,
  onCancel,
}: ImageAssetPickerProps) {
  const { t, i18n } = useTranslation(['imageAssets', 'actions'])
  const modalTitle = title ?? t('imageAssets:picker.defaultTitle')
  const dateLocale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en-US'
  const [tab, setTab] = useState<'library' | 'external'>('library')
  const [items, setItems] = useState<ImagePickerListItem[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [summary, setSummary] = useState<ImagePickerListResponse['summary'] | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [facetLoading, setFacetLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchDraft, setSearchDraft] = useState('')
  const [sort, setSort] = useState<'recent' | 'oldest' | 'name'>('recent')
  const [resourceType, setResourceType] = useState<PickerResourceType>('all')
  const [source, setSource] = useState<PickerSourceFilter>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PICKER_DEFAULT_PAGE_SIZE)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null)
  const [selectedDetail, setSelectedDetail] = useState<ImagePickerDetail | null>(null)
  const [detailExtras, setDetailExtras] = useState<Pick<ImagePickerDetail, 'variants' | 'usage' | '_timings'> | null>(null)
  const [detailExtrasLoading, setDetailExtrasLoading] = useState(false)
  const [uploadJobs, setUploadJobs] = useState<UploadJob[]>([])
  /** Files for optimistic upload tiles — survives list refresh; cleared on dismiss / picker close. */
  const uploadPendingFilesRef = useRef<Map<string, File>>(new Map())
  const [confirming, setConfirming] = useState(false)
  // VistoeraTASK-00523 - team-mode folder navigation (server decides team mode).
  const folderApi = useImagePickerFolders(workspaceId, open)
  const teamEnabled = folderApi.teamEnabled
  const [scope, setScope] = useState<PickerTeamScope>({ kind: 'my_all' })
  const [folderModal, setFolderModal] = useState<FolderNameModalState | null>(null)
  /** Destination folder frozen at selection time (retry must reuse the original target). */
  const uploadTargetRef = useRef<Map<string, string | null>>(new Map())
  const teamQuery = teamEnabled ? teamQueryForScope(scope) : null
  const teamQueryKey = teamQuery ? JSON.stringify(teamQuery) : ''
  const teamQueryRef = useRef(teamQuery)
  teamQueryRef.current = teamQuery
  const teamCtxRef = useRef({
    enabled: false,
    scope,
    folders: folderApi.folders,
    userId: folderApi.data?.team?.user_id ?? null,
  })
  teamCtxRef.current = {
    enabled: teamEnabled,
    scope,
    folders: folderApi.folders,
    userId: folderApi.data?.team?.user_id ?? null,
  }
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileSessionMeta, setMobileSessionMeta] = useState<MobileCaptureSessionMeta | null>(null)
  const searchTimer = useRef<number | null>(null)
  const openInitializedRef = useRef(false)
  const listReqRef = useRef(0)
  const facetReqRef = useRef(0)
  const detailReqRef = useRef(0)
  const familyByPickerIdRef = useRef<Record<string, string | null>>({})
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const pendingDeleteAssetIdsRef = useRef(new Set<string>())
  const deleteApiRejectedIdsRef = useRef(new Set<string>())
  const inFlightDeleteCountRef = useRef(0)
  const skipListLoadingOnceRef = useRef(false)
  const knownPickerIdsRef = useRef<Set<string>>(new Set())
  const mobilePollGenerationRef = useRef(0)
  const mobileCompletionSessionRef = useRef<string | null>(null)
  const mobileSessionMetaRef = useRef<MobileCaptureSessionMeta | null>(null)
  mobileSessionMetaRef.current = mobileSessionMeta

  const markPickerTiming = useCallback((name: string) => {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV && typeof performance?.mark === 'function') {
      performance.mark(name)
    }
  }, [])

  const loadFacets = useCallback(async (options?: { silent?: boolean }) => {
    if (!workspaceId) return
    const reqId = ++facetReqRef.current
    if (!options?.silent) {
      setFacetLoading(true)
    }
    markPickerTiming('iap-facet-start')
    try {
      const teamScopeQuery = teamQueryRef.current
      const { data } = await getImagePickerSummary(workspaceId, {
        site_id: siteId,
        ...(teamScopeQuery ?? {}),
      })
      if (facetReqRef.current !== reqId) return
      setSummary(data.summary)
      // Fast first page leaves total unknown until facets arrive (unfiltered only).
      setTotal((prev) => (prev == null ? data.total : prev))
      // Stale-while-revalidate: next Picker open for this workspace/site shows
      // this immediately instead of a blank sidebar while re-fetching.
      // (Never cached for a team scope - the cache key is scope-less.)
      if (!teamScopeQuery) {
        setCachedPickerCount(pickerCountCacheKey(workspaceId, siteId, true), {
          total: data.total,
          summary: data.summary,
        })
      }
      markPickerTiming('iap-facet-end')
    } catch {
      if (facetReqRef.current !== reqId) return
      // Facet failure must not clear list — leave counts as unknown (—).
      setSummary(null)
    } finally {
      if (facetReqRef.current === reqId && !options?.silent) setFacetLoading(false)
    }
    // teamQueryKey: a scope change must produce a fresh loader (and thus a reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, siteId, markPickerTiming, teamQueryKey])

  const loadGallery = useCallback(async (
    overrides?: GalleryQueryOverrides,
  ): Promise<{ items: ImagePickerListItem[]; total: number | null } | undefined> => {
    if (!workspaceId) return undefined
    const qPage = overrides?.page ?? page
    const qPageSize = overrides?.pageSize ?? pageSize
    const qSort = overrides?.sort ?? sort
    const qResourceType = overrides?.resourceType ?? resourceType
    const qSource = overrides?.source ?? source
    const qSearch = overrides?.search ?? search
    const silent = overrides?.silent || skipListLoadingOnceRef.current
    if (skipListLoadingOnceRef.current) skipListLoadingOnceRef.current = false
    const reqId = ++listReqRef.current
    if (!silent) {
      setListLoading(true)
      setError(null)
    }
    markPickerTiming('iap-list-start')
    const uploadTilePagination = tab === 'library'
    const listParams = pickerListRequestParams(qPage, qPageSize, uploadTilePagination)
    try {
      const { data } = await listImagePickerItems(workspaceId, {
        site_id: siteId,
        q: qSearch || undefined,
        resource_type: qResourceType === 'all' ? undefined : qResourceType,
        source: qSource === 'all' ? undefined : qSource,
        sort: qSort,
        page: listParams.page,
        page_size: listParams.page_size,
        offset: listParams.offset,
        include_summary: false,
        ...(teamQueryRef.current ?? {}),
      })
      if (listReqRef.current !== reqId) return undefined
      setItems(data.items)
      const resolvedTotal = typeof data.total === 'number' ? data.total : null
      if (typeof data.total === 'number') {
        setTotal(data.total)
      } else if (data.fast_page) {
        setTotal(null)
      }
      markPickerTiming('iap-list-end')
      if (typeof import.meta !== 'undefined' && import.meta.env?.DEV && typeof performance?.measure === 'function') {
        try {
          performance.measure('iap-list-api', 'iap-list-start', 'iap-list-end')
        } catch {
          /* mark missing */
        }
      }
      return { items: data.items, total: resolvedTotal }
    } catch (err) {
      if (listReqRef.current !== reqId) return undefined
      // Silent reconcile (post-delete) must not poison the grid with a global
      // transport error or wipe optimistic rows — match VideoAssetPicker.
      if (!silent) {
        setError((err as SaasApiError).message || t('imageAssets:picker.toast.loadFailed'))
        setItems([])
      }
      return undefined
    } finally {
      if (listReqRef.current === reqId && !silent) setListLoading(false)
    }
    // teamQueryKey: scope (view / folder) is part of the query identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, siteId, search, resourceType, source, sort, page, pageSize, tab, t, markPickerTiming, teamQueryKey])

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false
      listReqRef.current += 1
      facetReqRef.current += 1
      setListLoading(false)
      setFacetLoading(false)
      return
    }
    if (openInitializedRef.current) return
    openInitializedRef.current = true
    markPickerTiming('iap-picker-open')
    setTab('library')
    const initial = initialSelection.slice(0, maxSelection)
    setSelectedIds(initial)
    setActiveAssetId(initial[0] ?? null)
    setPage(1)
    setSearch('')
    setSearchDraft('')
    setResourceType('all')
    setSource('all')
    setSort('recent')
    setScope({ kind: 'my_all' })
    setFolderModal(null)
    uploadTargetRef.current.clear()
    // Stale-while-revalidate (P7): show the last-known count for this exact
    // workspace/site scope immediately instead of a blank sidebar; loadFacets
    // (fired in parallel with loadGallery below) revalidates it right after.
    const cached = getCachedPickerCount(pickerCountCacheKey(workspaceId, siteId, true))
    setSummary(cached?.summary ?? null)
    setTotal(cached?.total ?? null)
    setItems([])
    setError(null)
    setDetailExtras(null)
    setSelectedDetail(null)
    setUploadJobs([])
  }, [open, initialSelection, maxSelection, markPickerTiming, workspaceId, siteId])

  useEffect(() => {
    if (!open || tab !== 'library' || !openInitializedRef.current) return
    void loadGallery()
  }, [open, tab, loadGallery])

  useEffect(() => {
    if (!open || tab !== 'library' || !openInitializedRef.current) return
    void loadFacets()
  }, [open, tab, loadFacets])

  const familyForPickerId = useCallback(
    (pickerItemId: string): string | null => {
      const fromGallery = items.find((item) => item.id === pickerItemId)?.family_id
      if (fromGallery) return fromGallery
      return familyByPickerIdRef.current[pickerItemId] ?? null
    },
    [items],
  )

  const rememberFamily = useCallback((pickerItemId: string, familyId: string | null | undefined) => {
    if (familyId) familyByPickerIdRef.current[pickerItemId] = familyId
  }, [])

  // Detail only for activeAssetId — never N+1 for all selectedIds.
  useEffect(() => {
    if (!open || !activeAssetId) {
      setSelectedDetail(null)
      setDetailExtras(null)
      setDetailExtrasLoading(false)
      return
    }
    const pickerItemId = activeAssetId
    const reqId = ++detailReqRef.current
    setDetailExtrasLoading(true)
    setDetailExtras(null)
    if (typeof performance.mark === 'function') {
      performance.mark('iap-inspector-detail-start')
    }
    getImagePickerItemDetail(workspaceId, pickerItemId, { include_usage: true, include_variants: true })
      .then((r) => {
        if (detailReqRef.current !== reqId) return
        setSelectedDetail(r.data)
        rememberFamily(pickerItemId, r.data.family_id)
        setDetailExtras({
          variants: r.data.variants,
          usage: r.data.usage,
          _timings: r.data._timings,
        })
        if (typeof performance.mark === 'function') {
          performance.mark('iap-inspector-detail-end')
          performance.measure('iap-inspector-detail-api', 'iap-inspector-detail-start', 'iap-inspector-detail-end')
        }
      })
      .catch((err) => {
        if (detailReqRef.current !== reqId) return
        setSelectedDetail(null)
        setDetailExtras(null)
        if ((err as SaasApiError).status === 404) {
          setActiveAssetId(null)
        }
      })
      .finally(() => {
        if (detailReqRef.current !== reqId) return
        setDetailExtrasLoading(false)
      })
  }, [open, activeAssetId, workspaceId, rememberFamily])

  const selectPickerItem = useCallback(
    (pickerItemId: string, familyId?: string | null) => {
      const chainKey = familyId ?? familyForPickerId(pickerItemId)
      setActiveAssetId(pickerItemId)
      if (selectionMode === 'single') {
        setSelectedIds([pickerItemId])
        return
      }
      setSelectedIds((prev) => {
        if (prev.includes(pickerItemId)) return prev.filter((x) => x !== pickerItemId)
        const withoutChain = chainKey
          ? prev.filter((id) => {
              const otherChain = familyForPickerId(id)
              return otherChain !== chainKey
            })
          : prev
        if (withoutChain.length >= maxSelection) {
          message.warning(t('imageAssets:picker.selection.maxWarning', { max: maxSelection }))
          return prev
        }
        return [...withoutChain, pickerItemId]
      })
    },
    [selectionMode, maxSelection, familyForPickerId, t],
  )

  const toggleSelect = (id: string) => {
    const item = items.find((i) => i.id === id)
    rememberFamily(id, item?.family_id)
    selectPickerItem(id, item?.family_id)
  }

  // VistoeraTASK-00068 — after every in-flight delete settles (success or
  // failure), re-fetch the *current* page + facets from the canonical
  // dataset. This is what actually refills the grid gap: the offset-based
  // list query re-slices the (now smaller) dataset, pulling forward
  // whatever used to sit on the next page. A failed delete is corrected
  // for free here too — the asset was never actually removed server-side,
  // so this same re-fetch restores it (no separate rollback math needed).
  const reconcileAfterDelete = useCallback(async () => {
    const hasUploadTile = tab === 'library'
    const [listResult] = await Promise.all([
      loadGallery({ silent: true }),
      loadFacets({ silent: true }),
    ])
    if (listResult && typeof listResult.total === 'number') {
      const clamped = pickerPageAfterDelete(page, listResult.total, pageSize, hasUploadTile)
      if (clamped !== page) {
        // Current page no longer exists (e.g. deleted the last asset on the
        // last page) — clamp down. The page-change effect re-fetches it.
        skipListLoadingOnceRef.current = true
        setPage(clamped)
      }
    }
    return listResult
  }, [loadGallery, loadFacets, tab, page, pageSize])

  const handleDeleteAsset = (item: ImagePickerListItem) => {
    if (pendingDeleteAssetIdsRef.current.has(item.id)) return
    pendingDeleteAssetIdsRef.current.add(item.id)
    inFlightDeleteCountRef.current += 1

    // Instant optimistic feedback. Every update uses the functional setState
    // form so two rapid sequential deletes (P14) each apply against the
    // latest queued state rather than a stale snapshot captured at click
    // time — this is what actually prevents the race, not a request lock.
    setItems((prev) => prev.filter((i) => i.id !== item.id))
    setTotal((prev) => (prev == null ? prev : Math.max(0, prev - 1)))
    setSummary((prev) => decrementPickerSummary(prev, item))
    setSelectedIds((prev) => prev.filter((id) => id !== item.id))
    // Clearing activeAssetId (when it was the deleted item) already drives
    // the detail-panel effect above back to its empty state — no separate
    // selectedDetail/detailExtras bookkeeping needed here.
    setActiveAssetId((prev) => (prev === item.id ? null : prev))

    void deleteImagePickerItem(workspaceId, item.id)
      .catch((err) => {
        const saasErr = err as SaasApiError
        if (saasErr.status === 404) return
        deleteApiRejectedIdsRef.current.add(item.id)
      })
      .finally(() => {
        pendingDeleteAssetIdsRef.current.delete(item.id)
        inFlightDeleteCountRef.current = Math.max(0, inFlightDeleteCountRef.current - 1)
        if (inFlightDeleteCountRef.current === 0) {
          void (async () => {
            const listResult = await reconcileAfterDelete()
            const rejected = [...deleteApiRejectedIdsRef.current]
            deleteApiRejectedIdsRef.current.clear()
            if (rejected.length === 0) return
            const serverIds = new Set((listResult?.items ?? []).map((row) => row.id))
            const anyStillOnServer = rejected.some((id) => serverIds.has(id))
            if (anyStillOnServer) {
              message.error(t('imageAssets:toast.deleteFailedRestored'))
            }
          })()
        }
      })
  }

  const onSearchChange = (value: string) => {
    setSearchDraft(value)
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, 300)
  }

  const mobileUploadReady = Boolean(workspaceId && siteId)

  const openNativeFilePicker = () => {
    if (!mobileUploadReady) {
      message.error(t('imageAssets:picker.upload.mobileUnavailable'))
      return
    }
    fileInputRef.current?.click()
  }

  const openMobileCapture = () => {
    if (!mobileUploadReady) {
      message.error(t('imageAssets:picker.upload.mobileUnavailable'))
      return
    }
    if (mobileCaptureOpen) return
    mobilePollGenerationRef.current += 1
    mobileCompletionSessionRef.current = null
    knownPickerIdsRef.current = new Set()
    setMobileSessionMeta(null)
    setMobileCaptureOpen(true)
  }

  /** VistoeraTASK-00463 — insert ONE completed upload into the visible gallery
   * immediately (per-file incremental UI). Never waits for siblings in the
   * same batch. Uses functional `setItems` + `mergeUploadedIntoRecentList`
   * so a later `loadGallery` cannot silently drop already-promoted rows. */
  const promoteUploadedPickerItem = useCallback(
    async (pickerItemId: string): Promise<boolean> => {
      setSort('recent')
      setSearch('')
      setSearchDraft('')
      setPage(1)

      const next = selectionAfterUploadBatch({
        mode: selectionMode,
        maxSelection,
        previousSelectedIds: selectedIdsRef.current,
        successes: [{ pickerItemId }],
      })
      setSelectedIds(next.selectedIds)
      setActiveAssetId(next.activeAssetId)

      const filterQuery = { resourceType, source, search: '' }
      try {
        const { data: detail } = await getImagePickerItemDetail(workspaceId, pickerItemId, {
          include_usage: false,
          include_variants: false,
        })
        rememberFamily(pickerItemId, detail.family_id)
        let listItem = pickerDetailToListItem(detail)
        if (!pickerItemMatchesFilters(listItem, filterQuery)) return false
        const teamCtx = teamCtxRef.current
        if (teamCtx.enabled) {
          // Only merge into the visible grid when the browsed scope would list it
          // (a phone capture lands in 未整理图片, never inside an unrelated folder).
          if (!itemMatchesScope(listItem, teamCtx.scope, teamCtx.folders, teamCtx.userId)) return false
          if (listItem.created_by_user_id && listItem.created_by_user_id === teamCtx.userId) {
            listItem = {
              ...listItem,
              creator: { user_id: teamCtx.userId, display_name: t('imageAssets:folders.card.me'), is_me: true },
            }
          }
        }
        const firstPageLimit = pickerListLimit(1, pageSize, true)
        setItems((prev) => mergeUploadedIntoRecentList(prev, [listItem], firstPageLimit))
        setTotal((prevTotal) => (typeof prevTotal === 'number' ? prevTotal + 1 : prevTotal))
        return true
      } catch {
        return false
      }
    },
    [
      maxSelection,
      pageSize,
      rememberFamily,
      resourceType,
      selectionMode,
      source,
      t,
      workspaceId,
    ],
  )

  /** Mobile-capture completion may deliver several new ids at once — promote
   * each immediately, then one silent server reconcile + toast. */
  const finalizeUploadedPickerItems = useCallback(
    async (pickerItemIds: string[]) => {
      if (pickerItemIds.length === 0) return
      let promoted = 0
      for (const pickerItemId of pickerItemIds) {
        if (await promoteUploadedPickerItem(pickerItemId)) promoted += 1
      }
      const postUploadQuery: GalleryQueryOverrides = {
        page: 1,
        pageSize,
        sort: 'recent',
        resourceType,
        source,
        search: '',
      }
      await Promise.all([loadGallery({ ...postUploadQuery, silent: true }), loadFacets({ silent: true })])
      if (promoted > 0) {
        message.success(t('imageAssets:picker.upload.batchSuccess', { count: promoted }))
      }
    },
    [loadFacets, loadGallery, pageSize, promoteUploadedPickerItem, resourceType, source, t],
  )

  useEffect(() => {
    if (!open || !mobileCaptureOpen || tab !== 'library') return
    if (!isMobileCaptureSessionPaired(mobileSessionMeta)) return

    const generation = mobilePollGenerationRef.current
    const activeSessionId = mobileSessionMeta!.sessionId
    const sessionCreatedAt = mobileSessionMeta!.createdAt
    let baselineReady = false
    let cancelled = false

    const tick = async () => {
      if (cancelled || generation !== mobilePollGenerationRef.current) return
      const meta = mobileSessionMetaRef.current
      if (!meta || meta.sessionId !== activeSessionId) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      const result = await loadGallery({ page: 1, sort: 'recent', search: '', silent: true })
      if (!result || cancelled || generation !== mobilePollGenerationRef.current) return
      if (mobileSessionMetaRef.current?.sessionId !== activeSessionId) return

      if (!baselineReady) {
        knownPickerIdsRef.current = new Set(result.items.map((item) => item.id))
        baselineReady = true
        return
      }

      const newIds = filterSessionScopedNewPickerIds(result.items, knownPickerIdsRef.current, sessionCreatedAt)
      if (newIds.length === 0) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      newIds.forEach((id) => knownPickerIdsRef.current.add(id))
      mobileCompletionSessionRef.current = activeSessionId
      await finalizeUploadedPickerItems(newIds)
      setMobileCaptureOpen(false)
    }

    const timer = window.setInterval(() => {
      void tick()
    }, MOBILE_CAPTURE_POLL_MS)
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [finalizeUploadedPickerItems, loadGallery, mobileCaptureOpen, mobileSessionMeta, open, tab])

  const patchUploadJob = useCallback((localId: string, patch: Partial<UploadJob>) => {
    setUploadJobs((prev) => prev.map((j) => (j.localId === localId ? { ...j, ...patch } : j)))
  }, [])

  const ingestOnePickerFile = useCallback(
    async (file: File, localId: string) => {
      try {
        const { id } = await uploadSiteAssetIngest(
          workspaceId,
          siteId,
          file,
          {
            onProgress: (percent, phase) => {
              patchUploadJob(localId, {
                progress: percent,
                status: phase === 'done' ? 'done' : phase,
              })
            },
          },
          undefined,
          // Canonical single-upload surface; only the destination folder is added.
          { collection_id: uploadTargetRef.current.get(localId) ?? null },
        )
        patchUploadJob(localId, { progress: 100, status: 'done' })
        uploadPendingFilesRef.current.delete(localId)
        return { ok: true as const, pickerItemId: `sa:${id}`, localId }
      } catch (err) {
        const msg = siteAssetUploadErrorMessage(err, t('imageAssets:picker.upload.failed'))
        patchUploadJob(localId, { status: 'error', error: msg, progress: 100 })
        return { ok: false as const, localId, error: msg }
      }
    },
    [patchUploadJob, siteId, t, workspaceId],
  )

  const runPickerUploadBatch = useCallback(
    async (files: File[], jobs: UploadJob[]) => {
      let successCount = 0
      let failCount = 0

      await mapWithConcurrency(
        files,
        CANONICAL_SITE_ASSET_UPLOAD_CONCURRENCY,
        async (file, index) => {
          const localId = jobs[index].localId
          const outcome = await ingestOnePickerFile(file, localId)
          if (outcome.ok) {
            successCount += 1
            await promoteUploadedPickerItem(outcome.pickerItemId)
            setUploadJobs((prev) => prev.filter((j) => j.localId !== localId))
          } else {
            failCount += 1
          }
          return outcome
        },
      )

      if (successCount > 0) {
        void loadFacets({ silent: true })
      }

      if (successCount > 0 && failCount > 0) {
        message.warning(
          t('imageAssets:picker.upload.batchPartial', { ok: successCount, fail: failCount }),
        )
      } else if (successCount > 0) {
        message.success(t('imageAssets:picker.upload.batchSuccess', { count: successCount }))
      } else if (failCount > 0) {
        message.error(t('imageAssets:picker.upload.batchFailed', { count: failCount }))
      }
    },
    [ingestOnePickerFile, loadFacets, promoteUploadedPickerItem, t],
  )

  const handleFilesSelected = async (ev: ChangeEvent<HTMLInputElement>) => {
    const fileList = ev.target.files
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)
    ev.target.value = ''

    const jobs: UploadJob[] = files.map((f, i) => ({
      localId: `up-${Date.now()}-${i}-${f.name}`,
      fileName: f.name,
      progress: 8,
      status: 'queued',
    }))
    // Team mode: upload into the folder being browsed (全部图片 etc. -> 未整理图片).
    const uploadTarget = teamEnabled ? uploadTargetForScope(scope) : null
    for (let i = 0; i < files.length; i += 1) {
      uploadPendingFilesRef.current.set(jobs[i].localId, files[i])
      uploadTargetRef.current.set(jobs[i].localId, uploadTarget)
    }
    if (teamEnabled) {
      const nextScope = scopeAfterLocalUpload(scope)
      if (nextScope !== scope) setScope(nextScope)
    }
    setUploadJobs((prev) => [
      ...jobs,
      ...prev.filter((j) => isActiveUploadJob(j) || j.status === 'error'),
    ])
    await runPickerUploadBatch(files, jobs)
  }

  const retryUploadJob = useCallback(
    async (localId: string) => {
      const file = uploadPendingFilesRef.current.get(localId)
      if (!file) {
        message.warning(t('imageAssets:picker.upload.retryUnavailable'))
        return
      }
      patchUploadJob(localId, { status: 'queued', progress: 8, error: undefined })
      const outcome = await ingestOnePickerFile(file, localId)
      if (outcome.ok) {
        await promoteUploadedPickerItem(outcome.pickerItemId)
        setUploadJobs((prev) => prev.filter((j) => j.localId !== localId))
        void loadFacets({ silent: true })
        message.success(t('imageAssets:picker.upload.success'))
      }
    },
    [ingestOnePickerFile, loadFacets, patchUploadJob, promoteUploadedPickerItem, t],
  )

  const dismissUploadJob = useCallback((localId: string) => {
    uploadPendingFilesRef.current.delete(localId)
    setUploadJobs((prev) => prev.filter((j) => j.localId !== localId))
  }, [])

  // VistoeraTASK-00591 — HTML5 drag from thumbnail/list to canvas.
  // Browser-native drag threshold separates click (toggle select) from
  // drag; payload is always the single card under the pointer (never the
  // current multi-selection).
  const clearImageAssetDragChrome = useCallback(() => {
    setImageAssetDraggingChrome(false)
  }, [])

  useEffect(() => {
    if (!open) clearImageAssetDragChrome()
    return () => clearImageAssetDragChrome()
  }, [open, clearImageAssetDragChrome])

  const beginImageAssetCardDrag = useCallback(
    (e: DragEvent<HTMLButtonElement>, item: ImagePickerListItem) => {
      e.dataTransfer.setData(
        PCV_IMAGE_ASSET_DND_MIME,
        serializeImageAssetDragPayload({
          pickerItemId: item.id,
          filename: item.filename,
          thumbnailUrl: item.thumbnail_url,
        }),
      )
      e.dataTransfer.effectAllowed = 'copy'
      const thumb = e.currentTarget.querySelector('img')
      if (thumb instanceof HTMLImageElement && thumb.naturalWidth > 0) {
        try {
          e.dataTransfer.setDragImage(thumb, Math.min(40, thumb.clientWidth / 2), Math.min(40, thumb.clientHeight / 2))
        } catch {
          /* setDragImage is best-effort */
        }
      }
      setImageAssetDraggingChrome(true)
    },
    [],
  )

  const endImageAssetCardDrag = useCallback(() => {
    clearImageAssetDragChrome()
  }, [clearImageAssetDragChrome])

  const handleConfirm = async () => {
    if (selectedIds.length === 0) {
      message.warning(t('imageAssets:picker.toast.selectFirst'))
      return
    }
    setConfirming(true)
    try {
      // VistoeraTASK-00624 — object processing submits raw picker item ids to
      // the processing API. Do NOT resolve-to-SiteAsset first: that path
      // materializes library stages into new SiteAssets (duplicate storage).
      if (purpose === 'object_image_processing') {
        onConfirm(
          selectedIds.map((pickerItemId) => ({
            assetId: pickerItemId,
            pickerItemId,
            referenceType: 'site_asset' as const,
            filename: items.find((it) => it.id === pickerItemId)?.filename ?? null,
          })),
        )
        return
      }
      const target = resolveTarget(purpose)
      const resolved: ImageAssetPickerSelection[] = []
      for (const pickerItemId of selectedIds) {
        const { data } = await resolveImagePickerSelection(workspaceId, {
          picker_item_id: pickerItemId,
          target,
          site_id: siteId,
        })
        resolved.push({
          assetId: data.asset_id,
          pickerItemId,
          referenceType: data.reference_type as ImageAssetPickerSelection['referenceType'],
          filename: items.find((it) => it.id === pickerItemId)?.filename ?? null,
        })
      }
      onConfirm(resolved)
    } catch (err) {
      message.error((err as SaasApiError).message || t('imageAssets:picker.toast.confirmFailed'))
    } finally {
      setConfirming(false)
    }
  }

  const resourceCounts = useMemo(() => summary?.resource_types ?? null, [summary])
  const sourceCounts = useMemo(() => summary?.sources ?? null, [summary])

  const formatFacetCount = (counts: Record<string, number> | null, value: string): string => {
    if (facetLoading || counts == null) return '—'
    const n = counts[value]
    return typeof n === 'number' ? String(n) : '—'
  }

  const galleryHeadLabel =
    listLoading && items.length === 0
      ? t('imageAssets:picker.gallery.loading')
      : total == null
        ? t('imageAssets:picker.gallery.totalUnknown')
        : t('imageAssets:picker.gallery.total', { count: total })

  const handleCloseAssetPicker = useCallback(() => {
    mobilePollGenerationRef.current += 1
    mobileCompletionSessionRef.current = null
    knownPickerIdsRef.current = new Set()
    setMobileSessionMeta(null)
    setMobileCaptureOpen(false)
    setSelectedIds([])
    setActiveAssetId(null)
    setSelectedDetail(null)
    setDetailExtras(null)
    uploadPendingFilesRef.current.clear()
    setUploadJobs([])
    onCancel()
  }, [onCancel])

  const resolvedConfirmLabel = useMemo(() => {
    if (typeof confirmLabelProp === 'function') return confirmLabelProp(selectedIds.length)
    if (typeof confirmLabelProp === 'string' && confirmLabelProp.length > 0) return confirmLabelProp
    return defaultConfirmLabel(purpose, selectedIds.length, t)
  }, [confirmLabelProp, purpose, selectedIds.length, t])

  const uploadingCount = uploadJobs.filter((j) => isActiveUploadJob(j)).length
  const uploadTotalVisible = uploadJobs.filter((j) => j.status !== 'done').length

  const renderAssetCard = (item: ImagePickerListItem) => {
    const selected = selectedIds.includes(item.id)
    const active = activeAssetId === item.id
    const orderIndex = selectedIds.indexOf(item.id)
    return (
      <button
        key={item.id}
        type="button"
        className={`iap-card ${selected ? 'selected' : ''} ${active ? 'active' : ''}`}
        data-testid="iap-card"
        data-asset-id={item.id}
        {...vistoeraDndProps('image-asset')}
        draggable
        onDragStart={(e) => beginImageAssetCardDrag(e, item)}
        onDragEnd={endImageAssetCardDrag}
        onClick={() => toggleSelect(item.id)}
      >
        <div className="iap-card-thumb">
          {item.thumbnail_url ? (
            <img src={item.thumbnail_url} alt={item.filename} loading="lazy" draggable={false} />
          ) : (
            <span>—</span>
          )}
          {selected && (
            <span className="iap-card-check">
              {selectionMode === 'multiple' && orderIndex >= 0 ? orderIndex + 1 : <CheckOutlined />}
            </span>
          )}
          <span onClick={(e) => e.stopPropagation()}>
            <VistoeraActionConfirm
              title={t('imageAssets:deleteConfirm.singleTitle')}
              description={
                <>
                  {t('imageAssets:deleteConfirm.singleDescription')}
                  <br />
                  {t('imageAssets:picker.delete.filenameLabel', { filename: item.filename })}
                </>
              }
              confirmLabel={t('actions:delete')}
              tooltip={t('imageAssets:picker.delete.tooltip')}
              onConfirm={() => handleDeleteAsset(item)}
            >
              <span
                className="iap-card-delete"
                role="button"
                tabIndex={0}
                aria-label={t('imageAssets:picker.delete.aria')}
                onKeyDown={(ev: KeyboardEvent) => {
                  if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation()
                }}
              >
                <DeleteOutlined />
              </span>
            </VistoeraActionConfirm>
          </span>
          <span className="iap-card-badge">{item.resource_type_label}</span>
          {teamEnabled ? (
            <span
              className={`iap-card-share${item.visibility === 'private' ? ' is-private' : ''}`}
              data-testid="iap-card-share"
            >
              {item.visibility === 'private'
                ? t('imageAssets:folders.card.private')
                : t('imageAssets:folders.card.teamShared')}
            </span>
          ) : null}
        </div>
        <div className="iap-card-meta">
          <span className="iap-card-name" title={item.filename}>
            {item.filename}
          </span>
          <span className="iap-card-sub">
            {item.width && item.height ? `${item.width}×${item.height}` : '—'} · {item.format}
          </span>
          {teamEnabled ? (
            <>
              {/* creator === undefined: row came from a pre-team (unscoped) response - show
                  nothing rather than a misleading "unknown"; null = genuinely no creator. */}
              {item.creator === undefined ? null : (
              <span className="iap-card-creator" data-testid="iap-card-creator">
                {(() => {
                  const name = item.creator
                    ? item.creator.is_me
                      ? t('imageAssets:folders.card.me')
                      : item.creator.display_name
                    : t('imageAssets:team.creator.unknown')
                  return (
                    <>
                      <span className="iap-card-avatar" aria-hidden>
                        {(name.trim()[0] || '?').toUpperCase()}
                      </span>
                      <span className="iap-card-creator-name" title={name}>
                        {name}
                      </span>
                    </>
                  )
                })()}
              </span>
              )}
              <span className="iap-card-sub">{formatPickerCreatedAt(item.created_at, dateLocale)}</span>
            </>
          ) : null}
        </div>
      </button>
    )
  }

  const renderAssetListRow = (item: ImagePickerListItem) => {
    const selected = selectedIds.includes(item.id)
    const active = activeAssetId === item.id
    const orderIndex = selectedIds.indexOf(item.id)
    const dimensions = item.width && item.height ? `${item.width}×${item.height}` : '—'
    return (
      <button
        key={item.id}
        type="button"
        className={`iap-list-row${selected ? ' selected' : ''}${active ? ' active' : ''}`}
        data-testid="iap-list-row"
        data-asset-id={item.id}
        {...vistoeraDndProps('image-asset')}
        draggable
        onDragStart={(e) => beginImageAssetCardDrag(e, item)}
        onDragEnd={endImageAssetCardDrag}
        onClick={() => toggleSelect(item.id)}
      >
        <span className="iap-list-check">
          {selected ? (
            selectionMode === 'multiple' && orderIndex >= 0 ? orderIndex + 1 : <CheckOutlined />
          ) : null}
        </span>
        <span className="iap-list-thumb">
          {item.thumbnail_url ? (
            <img src={item.thumbnail_url} alt="" loading="lazy" draggable={false} />
          ) : (
            <PictureOutlined />
          )}
        </span>
        <span className="iap-list-name" title={item.filename}>{item.filename}</span>
        <span className="iap-list-type">{item.resource_type_label}</span>
        <span className="iap-list-dimensions">{dimensions}</span>
        <span className="iap-list-format">{item.format}</span>
        <span className="iap-list-source">{item.source_label}</span>
        <span className="iap-list-created">{formatPickerCreatedAt(item.created_at, dateLocale)}</span>
        <span onClick={(e) => e.stopPropagation()}>
          <VistoeraActionConfirm
            title={t('imageAssets:deleteConfirm.singleTitle')}
            description={
              <>
                {t('imageAssets:deleteConfirm.singleDescription')}
                <br />
                {t('imageAssets:picker.delete.filenameLabel', { filename: item.filename })}
              </>
            }
            confirmLabel={t('actions:delete')}
            tooltip={t('imageAssets:picker.delete.tooltip')}
            onConfirm={() => handleDeleteAsset(item)}
          >
            <span
              className="iap-list-delete"
              role="button"
              tabIndex={0}
              aria-label={t('imageAssets:picker.delete.aria')}
              onKeyDown={(ev: KeyboardEvent) => {
                if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation()
              }}
            >
              <DeleteOutlined />
            </span>
          </VistoeraActionConfirm>
        </span>
      </button>
    )
  }

  const uploadJobsForPage =
    page === 1 ? uploadJobs.filter((j) => isActiveUploadJob(j) || j.status === 'error') : []

  // ---- VistoeraTASK-00523: team-mode folder navigation -----------------------
  const currentFolderId = scope.kind === 'folder' ? scope.folderId : null
  const scopeCounts = folderApi.data?.scope_counts ?? null
  const selectScope = (next: PickerTeamScope) => {
    setScope(next)
    setPage(1)
  }
  const navigateFolder = (folderId: string | null) =>
    selectScope(folderId ? { kind: 'folder', folderId } : { kind: 'my_all' })
  const scopeRootLabel =
    scope.kind === 'my_created'
      ? t('imageAssets:folders.scope.myCreated')
      : scope.kind === 'team_all'
        ? t('imageAssets:folders.scope.teamShared')
        : scope.kind === 'team_members'
          ? t('imageAssets:folders.scope.teamMembers')
          : scope.kind === 'unsorted'
            ? t('imageAssets:folders.scope.unsorted')
            : t('imageAssets:folders.root')

  const submitFolderModal = async (name: string, st: FolderNameModalState) => {
    if (st.mode === 'create') {
      await folderApi.createFolder(name, st.parentId)
      message.success(t('imageAssets:folders.created'))
    } else {
      await folderApi.renameFolder(st.folderId, name)
      message.success(t('imageAssets:folders.renamed'))
    }
    setFolderModal(null)
  }

  const openCreateFolder = (parentId: string | null) => {
    const parent = parentId ? folderApi.folders.find((f) => f.id === parentId) : null
    setFolderModal({ mode: 'create', parentId, parentName: parent?.name ?? null })
  }

  const confirmDeleteFolder = (folder: { id: string; name: string }) => {
    Modal.confirm({
      title: t('imageAssets:folders.deleteTitle', { name: folder.name }),
      content: t('imageAssets:folders.deleteDescription'),
      okText: t('actions:delete'),
      okButtonProps: { danger: true },
      cancelText: t('actions:cancel'),
      zIndex: 2100,
      onOk: async () => {
        try {
          await folderApi.deleteFolder(folder.id)
          message.success(t('imageAssets:folders.deleted'))
          if (currentFolderId === folder.id) navigateFolder(null)
        } catch (err) {
          // Non-empty folders are refused server-side (no cascade) - surface the reason.
          message.error((err as SaasApiError).message || t('imageAssets:folders.deleteBlocked'))
        }
      },
    })
  }

  const scopeEntry = (key: PickerTeamScope['kind'], label: string, count: number | undefined) => (
    <button
      key={key}
      type="button"
      className={`iap-filter-item ${scope.kind === key ? 'active' : ''}`}
      onClick={() => selectScope({ kind: key } as PickerTeamScope)}
      data-testid={`iap-scope-${key}`}
    >
      <span>{label}</span>
      <span className="iap-filter-count">{typeof count === 'number' ? count : '—'}</span>
    </button>
  )

  const facetGroups = (
    <>
      <div className="iap-filter-group">
        <div className="iap-filter-title">{t('imageAssets:picker.filters.resourceType')}</div>
        {RESOURCE_FILTER_VALUES.map((value) => (
          <button
            key={value}
            type="button"
            className={`iap-filter-item ${resourceType === value ? 'active' : ''}`}
            onClick={() => {
              setResourceType(value)
              setPage(1)
            }}
          >
            <span>{resourceFilterLabel(t, value)}</span>
            <span className="iap-filter-count">{formatFacetCount(resourceCounts, value)}</span>
          </button>
        ))}
      </div>
      <div className="iap-filter-group">
        <div className="iap-filter-title">{t('imageAssets:picker.filters.source')}</div>
        {SOURCE_FILTER_VALUES.map((value) => (
          <button
            key={value}
            type="button"
            className={`iap-filter-item ${source === value ? 'active' : ''}`}
            onClick={() => {
              setSource(value)
              setPage(1)
            }}
          >
            <span>{sourceFilterLabel(t, value)}</span>
            <span className="iap-filter-count">{formatFacetCount(sourceCounts, value)}</span>
          </button>
        ))}
      </div>
    </>
  )
  const teamInfo = folderApi.data?.team
  const teamWorkspaceName = teamInfo?.workspace_name || ''
  const teamIsManager = teamInfo?.role === 'owner' || teamInfo?.role === 'admin'

  return (
    <>
    <Modal
      open={open}
      onCancel={handleCloseAssetPicker}
      footer={null}
      width="min(96vw, 1280px)"
      className="iap-modal"
      rootClassName="iap-modal-root"
      destroyOnClose
      closable={false}
      maskClosable
      keyboard
      zIndex={2000}
    >
      <div className="iap-shell" data-iap-selection-mode={selectionMode}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="iap-file-input"
          data-iap-upload-input="true"
          onChange={(e) => void handleFilesSelected(e)}
        />

        <div className="iap-header">
          <h2 className="iap-title">{modalTitle}</h2>
          <button
            type="button"
            className="iap-close-btn"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              handleCloseAssetPicker()
            }}
            aria-label={t('imageAssets:picker.closeAria')}
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="iap-tabs">
          <button type="button" className={`iap-tab ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')}>
            {t('imageAssets:picker.tabs.library')}
          </button>
          <button type="button" className="iap-tab" disabled title={t('imageAssets:picker.tabs.comingSoon')}>
            {t('imageAssets:picker.tabs.externalLink')}
          </button>
        </div>

        {tab === 'library' && (
          <>
            <div className="iap-toolbar">
              <Input
                className="iap-search"
                placeholder={t('imageAssets:filters.searchPlaceholder')}
                value={searchDraft}
                onChange={(e) => onSearchChange(e.target.value)}
                allowClear
              />
              <Select
                value={sort}
                onChange={setSort}
                style={{ width: 140 }}
                options={[
                  { value: 'recent', label: t('imageAssets:filters.sortRecent') },
                  { value: 'oldest', label: t('imageAssets:picker.sort.oldest') },
                  { value: 'name', label: t('imageAssets:filters.sortName') },
                ]}
              />
              {teamEnabled ? (
                // Team layout gives the left rail to the folder tree; the resource /
                // source facets live behind this existing 筛选 button instead.
                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  overlayClassName="iap-filter-popover"
                  zIndex={2100}
                  content={<div className="iap-filter-popover-body" data-testid="iap-filter-popover">{facetGroups}</div>}
                >
                  <Button icon={<FilterOutlined />} data-testid="iap-filter-btn">
                    {t('imageAssets:picker.filters.filter')}
                  </Button>
                </Popover>
              ) : (
                <Button icon={<FilterOutlined />}>{t('imageAssets:picker.filters.filter')}</Button>
              )}
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('grid')}
                aria-label={t('imageAssets:view.gridAria')}
                aria-pressed={viewMode === 'grid'}
                title={t('imageAssets:view.gridAria')}
              />
              <Button
                type={viewMode === 'list' ? 'primary' : 'default'}
                icon={<UnorderedListOutlined />}
                onClick={() => setViewMode('list')}
                aria-label={t('imageAssets:view.listAria')}
                aria-pressed={viewMode === 'list'}
                title={t('imageAssets:view.listAria')}
              />
              {uploadTotalVisible > 0 ? (
                <span className="iap-batch-progress">
                  {t('imageAssets:picker.upload.batchProgress', {
                    done: Math.max(0, uploadTotalVisible - uploadingCount),
                    total: uploadTotalVisible,
                  })}
                </span>
              ) : null}
            </div>

            <div className={`iap-body${teamEnabled ? ' is-team' : ''}`} data-iap-team-mode={teamEnabled ? 'true' : 'false'}>
              {teamEnabled ? (
                <aside className="iap-sidebar iap-team-sidebar" data-testid="iap-team-sidebar">
                  <div className="iap-team-ws">
                    <span className="iap-team-ws-avatar" aria-hidden>
                      {(teamWorkspaceName.trim()[0] || 'W').toUpperCase()}
                    </span>
                    <span className="iap-team-ws-name" title={teamWorkspaceName}>
                      {teamWorkspaceName}
                    </span>
                    <span className="iap-team-badge" data-testid="iap-team-badge">
                      {t('imageAssets:folders.scope.teamModeBadge')}
                    </span>
                  </div>
                  <div className="iap-team-hint" data-testid="iap-team-hint">
                    {teamIsManager
                      ? t('imageAssets:folders.scope.managerHint')
                      : t('imageAssets:folders.scope.memberHint')}
                  </div>
                  <div className="iap-filter-group">
                    <div className="iap-filter-title">{t('imageAssets:folders.scope.myLibrary')}</div>
                    {scopeEntry('my_all', t('imageAssets:folders.scope.allImages'), scopeCounts?.all)}
                    {scopeEntry('my_created', t('imageAssets:folders.scope.myCreated'), scopeCounts?.mine)}
                  </div>
                  <div className="iap-filter-group">
                    <div className="iap-filter-title">{t('imageAssets:folders.scope.teamShared')}</div>
                    {scopeEntry('team_all', t('imageAssets:folders.scope.allImages'), scopeCounts?.shared)}
                    {scopeEntry('team_members', t('imageAssets:folders.scope.teamMembers'), scopeCounts?.shared_by_others)}
                    {scopeEntry('unsorted', t('imageAssets:folders.scope.unsorted'), scopeCounts?.unsorted)}
                  </div>
                  <div className="iap-team-folders">
                    <ImageFolderTree
                      folders={folderApi.folders}
                      selectedFolderId={currentFolderId}
                      onSelect={(id) => navigateFolder(id)}
                      canManage={folderApi.canManage}
                      onCreate={openCreateFolder}
                      onRename={(f) => setFolderModal({ mode: 'rename', folderId: f.id, name: f.name })}
                      onDelete={confirmDeleteFolder}
                      testIdPrefix="iap-ift"
                    />
                    {folderApi.status === 'error' ? (
                      <div className="iap-team-folders-error" role="status" data-testid="iap-folders-error">
                        <span>{t('imageAssets:folders.loadFailed')}</span>
                        <Button size="small" type="link" onClick={() => void folderApi.reload()}>
                          {t('imageAssets:folders.retry')}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </aside>
              ) : (
                <aside className="iap-sidebar">{facetGroups}</aside>
              )}

              <section className="iap-gallery">
                {teamEnabled ? (
                  <div className="iap-gallery-head iap-team-head" data-testid="iap-team-head">
                    <ImageFolderBreadcrumb
                      folders={folderApi.folders}
                      currentFolderId={currentFolderId}
                      onNavigate={navigateFolder}
                      rootLabel={scopeRootLabel}
                      testIdPrefix="iap-crumb"
                    />
                    <span className="iap-team-head-actions">
                      {folderApi.canManage ? (
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          onClick={() => openCreateFolder(currentFolderId)}
                          data-testid="iap-new-folder"
                        >
                          {t('imageAssets:folders.createHere')}
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={openNativeFilePicker}
                        disabled={!mobileUploadReady}
                        data-testid="iap-upload-here"
                      >
                        {t('imageAssets:picker.upload.tileLabel')}
                      </Button>
                    </span>
                  </div>
                ) : (
                  <div className="iap-gallery-head">{galleryHeadLabel}</div>
                )}
                <div className="iap-gallery-scroll" data-iap-view-mode={viewMode}>
                  {error ? (
                    <Empty description={error}>
                      <Button icon={<ReloadOutlined />} onClick={() => void loadGallery()}>
                        {t('actions:retry')}
                      </Button>
                    </Empty>
                  ) : listLoading && items.length === 0 && uploadJobs.length === 0 ? (
                    <div className="iap-skeleton-grid" data-iap-list-loading="true">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="iap-skeleton-card" />
                      ))}
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="iap-grid" data-testid="iap-grid" data-iap-grid="true">
                      {page === 1 ? (
                        <AssetUploadActionTile
                          layout="grid"
                          mobileLabel={t('imageAssets:picker.upload.mobile')}
                          localLabel={t('imageAssets:picker.upload.local')}
                          tileLabel={t('imageAssets:picker.upload.tileLabel')}
                          onMobileClick={openMobileCapture}
                          onLocalClick={openNativeFilePicker}
                          mobileDisabled={!mobileUploadReady}
                          localDisabled={!mobileUploadReady}
                          testIdPrefix="image-asset-picker"
                          markerPrefix="iap"
                          gridMinHeight={188}
                        />
                      ) : null}

                      {uploadJobsForPage.map((job) => (
                        <div
                          key={job.localId}
                          className={`iap-card iap-uploading-tile ${job.status === 'error' ? 'is-error' : ''}`}
                          data-iap-uploading-tile="true"
                        >
                          <div className="iap-card-thumb">
                            <span className="iap-uploading-status">
                              {job.status === 'error'
                                ? job.error || t('imageAssets:picker.upload.failed')
                                : job.status === 'processing'
                                  ? t('imageAssets:picker.upload.processing')
                                  : job.status === 'queued'
                                    ? t('imageAssets:picker.upload.queued')
                                    : t('imageAssets:picker.upload.tileProgress', {
                                        name: job.fileName,
                                        percent: job.progress,
                                      })}
                            </span>
                            {isActiveUploadJob(job) ? (
                              <div className="iap-uploading-progress">
                                <span style={{ width: `${job.progress}%` }} />
                              </div>
                            ) : null}
                          </div>
                          <div className="iap-card-meta">
                            <span className="iap-card-name" title={job.fileName}>
                              {job.fileName}
                            </span>
                            <span className="iap-card-sub">
                              {job.status === 'error'
                                ? t('imageAssets:picker.upload.failed')
                                : job.status === 'processing'
                                  ? t('imageAssets:picker.upload.processing')
                                  : job.status === 'queued'
                                    ? t('imageAssets:picker.upload.queued')
                                    : t('imageAssets:picker.upload.uploading')}
                            </span>
                            {job.status === 'error' ? (
                              <div className="iap-upload-error-actions">
                                <Button size="small" type="link" onClick={() => void retryUploadJob(job.localId)}>
                                  {t('imageAssets:picker.upload.retry')}
                                </Button>
                                <Button size="small" type="link" onClick={() => dismissUploadJob(job.localId)}>
                                  {t('imageAssets:picker.upload.dismiss')}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}

                      {items.length === 0 && uploadJobs.length === 0 ? (
                        <Empty
                          description={
                            teamEnabled && scope.kind === 'folder'
                              ? t('imageAssets:folders.empty')
                              : t('imageAssets:empty.noAssets')
                          }
                          style={{ gridColumn: '1 / -1' }}
                        />
                      ) : null}

                      {items.map(renderAssetCard)}
                    </div>
                  ) : (
                    <div className="iap-list" data-testid="iap-list" data-iap-list="true">
                      {page === 1 ? (
                        <AssetUploadActionTile
                          layout="list"
                          mobileLabel={t('imageAssets:picker.upload.mobile')}
                          localLabel={t('imageAssets:picker.upload.local')}
                          tileLabel={t('imageAssets:picker.upload.tileLabel')}
                          onMobileClick={openMobileCapture}
                          onLocalClick={openNativeFilePicker}
                          mobileDisabled={!mobileUploadReady}
                          localDisabled={!mobileUploadReady}
                          testIdPrefix="image-asset-picker"
                          markerPrefix="iap"
                        />
                      ) : null}

                      {uploadJobsForPage.map((job) => (
                        <div
                          key={job.localId}
                          className={`iap-list-row iap-list-uploading ${job.status === 'error' ? 'is-error' : ''}`}
                          data-iap-uploading-tile="true"
                        >
                          <span className="iap-list-check" />
                          <span className="iap-list-thumb" />
                          <span className="iap-list-name" title={job.fileName}>{job.fileName}</span>
                          <span className="iap-list-type">—</span>
                          <span className="iap-list-dimensions">—</span>
                          <span className="iap-list-format">—</span>
                          <span className="iap-list-source">
                            {job.status === 'error'
                              ? t('imageAssets:picker.upload.failed')
                              : job.status === 'processing'
                                ? t('imageAssets:picker.upload.processing')
                                : job.status === 'queued'
                                  ? t('imageAssets:picker.upload.queued')
                                  : t('imageAssets:picker.upload.uploading')}
                          </span>
                          <span className="iap-list-created">
                            {job.status === 'error' ? (
                              <>
                                <Button size="small" type="link" onClick={() => void retryUploadJob(job.localId)}>
                                  {t('imageAssets:picker.upload.retry')}
                                </Button>
                                <Button size="small" type="link" onClick={() => dismissUploadJob(job.localId)}>
                                  {t('imageAssets:picker.upload.dismiss')}
                                </Button>
                              </>
                            ) : (
                              `${job.progress}%`
                            )}
                          </span>
                          <span />
                        </div>
                      ))}

                      {items.length === 0 && uploadJobs.length === 0 ? (
                        <Empty description={t('imageAssets:empty.noAssets')} />
                      ) : null}

                      {items.map(renderAssetListRow)}
                    </div>
                  )}
                </div>
              </section>

              <aside className="iap-detail">
                {!activeAssetId ? (
                  <Empty description={t('imageAssets:picker.empty.selectForDetail')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : selectedDetail ? (
                  <>
                    <div className="iap-detail-preview" data-iap-inspector-ready="true">
                      {selectedDetail.thumbnail_url ? (
                        <img src={selectedDetail.thumbnail_url} alt={selectedDetail.filename} loading="lazy" />
                      ) : null}
                    </div>
                    <div className="iap-detail-name">{selectedDetail.filename}</div>
                    <span className="iap-card-badge" style={{ position: 'static', display: 'inline-block' }}>
                      {selectedDetail.resource_type_label}
                    </span>
                    {selectionMode === 'multiple' && selectedIds.length > 1 ? (
                      <p className="iap-detail-hint">{t('imageAssets:picker.selection.multiHint')}</p>
                    ) : null}
                    <div className="iap-detail-section">
                      <h4>{t('imageAssets:picker.detail.basicInfo')}</h4>
                      <div className="iap-kv">
                        <span>{t('imageAssets:picker.detail.format')}</span>
                        <span>{selectedDetail.format}</span>
                        <span>{t('imageAssets:preview.meta.dimensions')}</span>
                        <span>
                          {selectedDetail.width && selectedDetail.height
                            ? `${selectedDetail.width} × ${selectedDetail.height}`
                            : '—'}
                        </span>
                        <span>{t('imageAssets:preview.meta.fileSize')}</span>
                        <span>{formatBytes(selectedDetail.size_bytes)}</span>
                        <span>{t('imageAssets:preview.meta.createdAt')}</span>
                        <span>
                          {selectedDetail.created_at
                            ? new Date(selectedDetail.created_at).toLocaleString(dateLocale, { hour12: false })
                            : '—'}
                        </span>
                        <span>{t('imageAssets:preview.meta.source')}</span>
                        <span>{selectedDetail.source_label}</span>
                      </div>
                    </div>
                    {detailExtrasLoading && !detailExtras ? (
                      <div className="iap-detail-section">
                        <h4>{t('imageAssets:picker.detail.extendedInfo')}</h4>
                        <div className="iap-skeleton-card" style={{ height: 48 }} />
                      </div>
                    ) : null}
                    {detailExtras?.usage ? (
                      <div className="iap-detail-section">
                        <h4>{t('imageAssets:picker.detail.usageInfo')}</h4>
                        <div className="iap-kv">
                          <span>{t('imageAssets:picker.detail.usageCount')}</span>
                          <span>{detailExtras.usage.usage_count}</span>
                        </div>
                      </div>
                    ) : null}
                    {detailExtras?.variants && detailExtras.variants.length > 1 ? (
                      <div className="iap-detail-section">
                        <h4>{t('imageAssets:preview.relatedVersions')}</h4>
                        <div className="iap-variants">
                          {detailExtras.variants.map((v) => (
                            <button
                              key={v.picker_item_id}
                              type="button"
                              className={`iap-variant-btn ${activeAssetId === v.picker_item_id ? 'selected' : ''}`}
                              title={v.resource_type_label}
                              onClick={() => selectPickerItem(v.picker_item_id, selectedDetail.family_id)}
                            >
                              {v.thumbnail_url ? <img src={v.thumbnail_url} alt={v.filename || ''} loading="lazy" /> : null}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {selectionMode === 'multiple' && selectedIds.length > 1 ? (
                      <div className="iap-detail-section">
                        <h4>{t('imageAssets:picker.selection.count', { count: selectedIds.length })}</h4>
                        <ol className="iap-multi-list">
                          {selectedIds.map((id, idx) => {
                            const item = items.find((i) => i.id === id)
                            return (
                              <li key={id}>
                                <span className="iap-multi-order">{idx + 1}</span>
                                <span>{item?.filename ?? id}</span>
                              </li>
                            )
                          })}
                        </ol>
                      </div>
                    ) : null}
                  </>
                ) : detailExtrasLoading ? (
                  <div className="iap-skeleton-card" style={{ height: 120 }} />
                ) : (
                  <Empty description={t('imageAssets:picker.empty.detailLoadFailed')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </aside>
            </div>

            <div className="iap-footer">
              <div className="iap-footer-left">
                {t('imageAssets:picker.selection.count', { count: selectedIds.length })}
                {selectedIds.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setSelectedIds([])
                      setActiveAssetId(null)
                    }}
                  >
                    {t('imageAssets:picker.selection.clear')}
                  </Button>
                )}
              </div>
              <div className="iap-footer-center">
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={
                    total == null
                      ? 0
                      : pickerAntdPaginationTotal(total, pageSize, tab === 'library')
                  }
                  showSizeChanger
                  pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
                  onChange={(p, ps) => {
                    setPage(p)
                    setPageSize(ps)
                  }}
                  showTotal={() =>
                    total == null
                      ? '—'
                      : `${page} / ${pickerTotalPages(total, pageSize, tab === 'library')}`
                  }
                  size="small"
                />
              </div>
              <div className="iap-footer-right">
                <Button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCloseAssetPicker()
                  }}
                >
                  {t('actions:cancel')}
                </Button>
                <Button type="primary" loading={confirming} disabled={selectedIds.length === 0} onClick={() => void handleConfirm()}>
                  {resolvedConfirmLabel}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
    <MobileCaptureQrModal
      open={mobileCaptureOpen}
      workspaceId={workspaceId}
      siteId={siteId}
      targetFlow="workspace_asset_library"
      uploadHintKey="mobileCapture.uploadHintAssetLibrary"
      onClose={() => setMobileCaptureOpen(false)}
      onSessionMetaChange={setMobileSessionMeta}
    />
    <FolderNameModal
      state={folderModal}
      onCancel={() => setFolderModal(null)}
      onSubmit={submitFolderModal}
      testIdPrefix="iap-folder"
    />
    </>
  )
}

export function ImageAssetPickerField({
  workspaceId,
  siteId,
  assetId,
  onChange,
  purpose = 'generic',
  aspectRatio = '16 / 9',
  label,
  compact = false,
}: {
  workspaceId: string
  siteId: string
  assetId: string | null
  onChange: (assetId: string | null) => void
  purpose?: ImageAssetPickerPurpose
  aspectRatio?: string
  label?: string
  compact?: boolean
}) {
  const { t } = useTranslation('imageAssets')
  const [open, setOpen] = useState(false)
  return (
    <>
      <div className={`ge-asset-field ${compact ? 'is-compact' : ''}`}>
        <div className="ge-asset-field-preview" style={{ aspectRatio }}>
          {assetId ? <img src={siteAssetVariantUrl(workspaceId, assetId, 'web')} alt="" /> : (
            <div className="ge-asset-field-empty">
              <PictureOutlined />
            </div>
          )}
        </div>
        <div className="ge-asset-field-actions">
          <button type="button" className="ge-btn" onClick={() => setOpen(true)}>
            {label || (assetId ? t('picker.field.replaceImage') : t('picker.field.selectImage'))}
          </button>
          {assetId && (
            <button type="button" className="ge-btn ge-btn-danger-text" onClick={() => onChange(null)}>
              {t('picker.field.remove')}
            </button>
          )}
        </div>
      </div>
      <ImageAssetPicker
        open={open}
        workspaceId={workspaceId}
        siteId={siteId}
        purpose={purpose}
        selectionMode="single"
        maxSelection={1}
        onCancel={() => setOpen(false)}
        onConfirm={(sel) => {
          onChange(sel[0]?.assetId ?? null)
          setOpen(false)
        }}
      />
    </>
  )
}
